import { useState } from "react";
import styles from "./style.module.css";

export type FilterType = "color" | "range" | "checkbox";

export interface FilterGroupData<T extends string = string> {
  title: T;
  type: FilterType;
  options?: string[]; // For checkbox
  colors?: string[]; // For color
  range?: [number, number]; // For range
}

export type FilterValues<T extends string> = {
  [K in T]?: string[] | [number, number];
};

interface FilterSidebarProps<T extends string> {
  filters: FilterGroupData<T>[];
  onChange?: (values: FilterValues<T>) => void;
  initialValues?: FilterValues<T>;
}

export const FilterSidebar = <T extends string>({
  filters,
  onChange,
  initialValues = {},
}: FilterSidebarProps<T>) => {
  const [open, setOpen] = useState<Record<T, boolean>>(
    Object.fromEntries(filters.map((f) => [f.title, true])) as Record<T, boolean>
  );

  const [selected, setSelected] = useState<FilterValues<T>>(initialValues);

  const handleUpdate = (title: T, value: string[] | [number, number]) => {
    const updated = { ...selected, [title]: value };
    setSelected(updated);
    onChange?.(updated);
  };

  const toggleSection = (name: T) => {
    setOpen((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  return (
    <aside className={styles.sidebar}>
      {filters.map((filter) => (
        <div key={filter.title} className={styles.section}>
          <button
            className={styles.header}
            onClick={() => toggleSection(filter.title)}
          >
            <span>{filter.title}</span>
            <span
              className={`${styles.arrow} ${open[filter.title] ? styles.open : ""}`}
            ></span>
          </button>

          {open[filter.title] && (
            <>
              {/* 🎨 Color Filter */}
              {filter.type === "color" && filter.colors && (
                <div className={styles.colorRow}>
                  {filter.colors.map((color) => {
                    const selectedColors =
                      (selected[filter.title] as string[] | undefined) ?? [];
                    const isActive = selectedColors.includes(color);

                    return (
                      <button
                        key={color}
                        onClick={() =>
                          handleUpdate(
                            filter.title,
                            isActive
                              ? selectedColors.filter((c) => c !== color)
                              : [...selectedColors, color]
                          )
                        }
                        className={`${styles.colorDot} ${
                          isActive ? styles.activeDot : ""
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    );
                  })}
                </div>
              )}

              {/* 💰 Range Filter */}
              {filter.type === "range" && filter.range && (
                <div className={styles.pricePanel}>
                  {(() => {
                    const currentRange =
                      (selected[filter.title] as [number, number] | undefined) ??
                      filter.range;

                    const [min, max] = currentRange;

                    return (
                      <>
                        <div className={styles.priceInputs}>
                          <input
                            type="number"
                            value={min}
                            onChange={(e) =>
                              handleUpdate(filter.title, [
                                +e.target.value,
                                max,
                              ])
                            }
                          />
                          <span>To</span>
                          <input
                            type="number"
                            value={max}
                            onChange={(e) =>
                              handleUpdate(filter.title, [
                                min,
                                +e.target.value,
                              ])
                            }
                          />
                        </div>
                        <input
                          type="range"
                          min={0}
                          max={10000}
                          value={min}
                          onChange={(e) =>
                            handleUpdate(filter.title, [
                              +e.target.value,
                              max,
                            ])
                          }
                          className={styles.slider}
                        />
                        <input
                          type="range"
                          min={0}
                          max={10000}
                          value={max}
                          onChange={(e) =>
                            handleUpdate(filter.title, [
                              min,
                              +e.target.value,
                            ])
                          }
                          className={styles.slider}
                        />
                      </>
                    );
                  })()}
                </div>
              )}

              {/* ☑️ Checkbox Filter */}
              {filter.type === "checkbox" && filter.options && (
                <div className={styles.optionList}>
                  {filter.options.map((option) => {
                    const selectedValues =
                      (selected[filter.title] as string[] | undefined) ?? [];
                    const checked = selectedValues.includes(option);

                    return (
                      <label key={option} className={styles.option}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const updated = checked
                              ? selectedValues.filter((o) => o !== option)
                              : [...selectedValues, option];
                            handleUpdate(filter.title, updated);
                          }}
                        />
                        <span>{option}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </aside>
  );
};
